import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllAppointmentsByDateRange } from "../../store/thunks/appointmentsThunk";
import { fetchAllPayrollsByStatusAndPayPeriod } from "../../store/thunks/payrollThunk";
import { fetchClinicians } from "../../store/thunks/cliniciansThunk";
import { fetchClinics } from "../../store/thunks/clinicThunk";
import { fetchPayrollCohorts, createPayrollCohort, updatePayrollCohort, deletePayrollCohort } from "../../store/thunks/payrollCohortThunk";
import HRIndividualPayrollPage from "./HRIndividualPayrollPage";
import PayrollScheduleTable from "./PayrollSchedule";
import payrollDates from "./payrollDates";
import PayrollStats from "./PayrollStats";
import * as XLSX from "xlsx";
import { toast } from 'react-toastify';
import { Listbox } from "@headlessui/react";
import {
  CheckIcon,
  ChevronUpDownIcon,
  XMarkIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  InformationCircleIcon,
} from "@heroicons/react/20/solid";
import { Dialog, Transition } from "@headlessui/react";

export default function HRPayrollOverviewPage() {
  const dispatch = useDispatch();
  const clinicians = useSelector((state) => state.clinicians.clinicians || []);
  const clinics = useSelector((state) => state.clinics.clinics || []);
  const payrollCohorts = useSelector((state) => state.payrollCohorts?.payrollCohorts || []);
  const [ishrpayrollpage, setishrpayrollpage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  
  // New filter states
  const [selectedPayrollCohort, setSelectedPayrollCohort] = useState(null); // null means "All"
  const [selectedClinicId, setSelectedClinicId] = useState("All");
  
  // Cohort management modal states
  const [showCohortModal, setShowCohortModal] = useState(false);
  const [editingCohort, setEditingCohort] = useState(null);
  const [cohortFormData, setCohortFormData] = useState({ name: '', description: '' });
  const [cohortModalLoading, setCohortModalLoading] = useState(false);

  // Reset clinic filter when cohort changes
  const handleCohortChange = (newCohort) => {
    setSelectedPayrollCohort(newCohort);
    setSelectedClinicId("All"); // Reset clinic selection when cohort changes
  };

  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0); // Start of the current day
    return date;
  });

  // currentDate is used in determining the default selected period
  const [currentDate, setCurrentDate] = useState(new Date());

  // Finds the pay period where the current date is between its start (from includeDates) and its paymentDate
  const findUpperBoundPayPeriod = () => {
    return payrollDates.find((period) => {
      const [startDateString] = period.includeDates.split("-");
      const startDate = new Date(Date.parse(startDateString.trim()));
      const paymentDate = new Date(Date.parse(period.paymentDate.trim()));
      paymentDate.setHours(23, 59, 59, 999); // Include the entire payment date
      return currentDate >= startDate && currentDate <= paymentDate;
    });
  };

  // Use the default pay period as a single source of truth
  const defaultSelectedPeriod = findUpperBoundPayPeriod();

  // Initialize selectedPeriod using the default and derive endDate from its includeDates
  const [selectedPeriod, setSelectedPeriod] = useState(defaultSelectedPeriod);
  const [endDate, setEndDate] = useState(() => {
    if (defaultSelectedPeriod) {
      const [, endDateString] = defaultSelectedPeriod.includeDates.split("-");
      const newEndDate = new Date(endDateString.trim());
      newEndDate.setUTCHours(23, 59, 59, 999);
      return newEndDate;
    }
    return new Date(); // Fallback if no period found
  });

  const [showPayrollSchedule, setShowPayrollSchedule] = useState(false);
  const [selectedClinician, setSelectedClinician] = useState(null);
  const [submissionDate, setSubmissionDate] = useState(new Date());
  const [sortOrder, setSortOrder] = useState("az");

  const allAppointments = useSelector(
    (state) => state.appointments.allPayrollAppointments || []
  );
  const allPayrolls = useSelector(
    (state) => state.payrolls.allCliniciansPayrolls || []
  );

  // When the period is changed via the Listbox selector, update startDate, endDate, selectedPeriod, and submissionDate accordingly.
  const handlePeriodChange = (selectedPeriod) => {
    setStartDate(
      new Date(`${selectedPeriod.includeDates.split("-")[0].trim()}`)
    );
    const newEndDate = new Date(
      `${selectedPeriod.includeDates.split("-")[1].trim()}`
    );
    newEndDate.setUTCHours(23, 59, 59, 999);
    setEndDate(newEndDate);
    setSelectedPeriod(selectedPeriod);
    setSubmissionDate(new Date(`${selectedPeriod.submissionDate}`));
  };

  const [inCarryover, setInCarryover] = useState(false);
  const [showInactiveClinicians, setShowInactiveClinicians] = useState(false);

  // Fetch data using the current selected period and endDate
  useEffect(() => {
    if (selectedPeriod) {
      setLoading(true);
      const fetchData = async () => {
        try {
          await Promise.all([
            dispatch(fetchClinicians()),
            dispatch(fetchClinics()),
            dispatch(fetchPayrollCohorts()),
            dispatch(fetchAllAppointmentsByDateRange({ 
              endDate,
              cohortId: selectedPayrollCohort?._id || null,
              clinicId: selectedClinicId === "All" ? null : selectedClinicId
            })), // endDate now aligns with the selected period
            dispatch(
              fetchAllPayrollsByStatusAndPayPeriod({
                statuses: [
                  "Pending",
                  "Ready",
                  "Approved",
                  "Paid to Clinician",
                  "Carryover",
                ],
                payPeriod: selectedPeriod.payPeriodNumber,
                cohortId: selectedPayrollCohort?._id || null,
                clinicId: selectedClinicId === "All" ? null : selectedClinicId,
              })
            ),
          ]);
          const adjustedSubmissionDate = new Date(submissionDate);
          adjustedSubmissionDate.setHours(23, 59, 59, 999);
          setInCarryover(currentDate > adjustedSubmissionDate);
        } catch (error) {
          console.error("Fetch data error:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [
    dispatch,
    startDate,
    endDate,
    selectedPeriod,
    currentDate,
    submissionDate,
    refreshKey,
    selectedPayrollCohort,
    selectedClinicId,
  ]);

  // Synchronize endDate whenever the selectedPeriod changes to always reflect its includeDates end
  useEffect(() => {
    if (selectedPeriod) {
      const [, endDateString] = selectedPeriod.includeDates.split("-");
      const newEndDate = new Date(endDateString.trim());
      newEndDate.setUTCHours(23, 59, 59, 999);
      setEndDate(newEndDate);
    }
  }, [selectedPeriod]);

  const handleClinicianClick = (clinician) => {
    if (!clinician || !clinician._id) {
      console.error("Invalid clinician object:", clinician);
      return; // Prevent opening the page if clinician is invalid
    }
    setSelectedClinician(clinician);
    setishrpayrollpage(true);
  };

  const handleCloseModal = () => {
    setShowPayrollSchedule(false);
  };

  const getBorderStyle = (data) => {
    const hasPendingAppointments = (data.pendingAppointments || []).length > 0;
    const hasReadyCompletedAppointments = (
      data.completedAppointments || []
    ).some((appointment) => appointment.status === "Ready");
    const hasPaidCompletedAppointments = (
      data.completedAppointments || []
    ).some((appointment) => appointment.status === "Paid to Clinician");

    if (hasReadyCompletedAppointments) {
      return "border-green-500 border-dashed";
    } else if (hasPaidCompletedAppointments) {
      return "border-green-500 border-solid";
    } else if (hasPendingAppointments) {
      return "border-yellow-500 border-dashed";
    } else {
      return "border-yellow-500 border-solid";
    }
  };

  const exportToSpreadsheet = (allPayrolls) => {
    if (!allPayrolls || allPayrolls.length === 0) {
      toast.warning("No data available to export.");
      return;
    }

    // Flatten the payrolls from all groups in localPayrolls
    const flattenedPayrolls = allPayrolls.flatMap((group) => group.payrolls);

    // Map the data to the desired format
    const data = flattenedPayrolls.map((payroll) => ({
      "payroll._id": payroll._id || "N/A",
      "appointment._id": payroll.appointmentId?._id || "N/A",
      "invoice._id": payroll.invoiceId?._id || "N/A",
      "Pay Period": payroll.payPeriod || "N/A",
      "Clinician First Name": payroll.clinicianId?.firstName || "N/A",
      "Clinician Last Name": payroll.clinicianId?.lastName || "N/A",
      "Appointment Title": payroll.appointmentId?.title || "N/A",
      "Appointment Start Date": payroll.appointmentId?.start
        ? new Date(payroll.appointmentId.start).toLocaleString("en-US")
        : "N/A",
      "Client First Name": payroll.appointmentId?.client?.firstName || "N/A",
      "Client Last Name": payroll.appointmentId?.client?.lastName || "N/A",
      "Invoice ID": payroll.invoiceId?.invoiceId || "N/A",
      "Invoice Amount": payroll.invoiceId?.amount || "N/A",
      "Percent Received": payroll.percentReceived || "N/A",
      "Payroll Amount": payroll.payrollAmount || "N/A",
    }));

    // Create a new workbook and worksheet
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Payroll Data");

    // Generate a dynamic file name
    const firstPayroll = flattenedPayrolls[0]; // Use the first payroll to derive the file name
    const fileName = `${
      firstPayroll.payPeriod || "UnknownPayPeriod"
    }_payroll_data.xlsx`;

    // Generate a download
    XLSX.writeFile(workbook, fileName);
  };

  const getSubmissionDate = () => {
    const currentPeriod = payrollDates.find(
      (period) => period.payPeriodNumber === selectedPeriod.payPeriodNumber
    );

    if (currentPeriod) {
      const submissionDate = new Date(currentPeriod.submissionDate);
      return submissionDate.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
      });
    } else {
      return "N/A";
    }
  };

  const getScheduledPayDay = () => {
    const currentPeriod = payrollDates.find(
      (period) => period.payPeriodNumber === selectedPeriod.payPeriodNumber
    );

    if (currentPeriod) {
      const paymentDate = new Date(currentPeriod.paymentDate);
      return paymentDate.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
      });
    } else {
      return "N/A";
    }
  };

  const calculatePendingAppointments = () => {
    if (!allAppointments || typeof allAppointments !== "object" || inCarryover)
      return 0;

    const totalPendingAppointments = Object.values(allAppointments).reduce(
      (total, clinicianData) => {
        const { appointments } = clinicianData;

        const clinicianPendingCount = appointments.filter((appointment) => {
          const hasSoapNotesComplete = appointment.history?.some(
            (historyItem) => historyItem.status === "Soap Notes Complete"
          );
          const hasInvoicePaid =
            appointment.invoice && appointment.invoice.status === "paid";

          return !(hasSoapNotesComplete && hasInvoicePaid);
        }).length;

        return total + clinicianPendingCount;
      },
      0
    );

    return totalPendingAppointments;
  };

  const calculateCompletedAppointments = (clinicianId = null) => {
    const { clinicianMetrics } = metrics;

    if (clinicianId) {
      return clinicianMetrics[clinicianId]?.completedAppointments || 0;
    }

    return totalCompletedAppointments;
  };

  const calculateCarryoverAppointments = () => {
    if (!inCarryover || typeof allAppointments !== "object") return 0;

    return Object.values(allAppointments).reduce(
      (total, clinicianData) => total + clinicianData.appointments.length,
      0
    );
  };

  const calculatePendingPay = () => {
    if (
      !allAppointments ||
      typeof allAppointments !== "object" ||
      inCarryover
    ) {
      return 0;
    }

    const totalPendingPay = Object.values(allAppointments || {}).reduce(
      (total, clinicianData) => {
        if (
          !clinicianData ||
          !clinicianData.appointments ||
          !clinicianData.clinician
        ) {
          return total;
        }

        const { appointments, clinician } = clinicianData;
        const payPercentage = clinician?.defaultPayPercentage
          ? clinician.defaultPayPercentage / 100
          : 0;

        const clinicianTotal = (appointments || []).reduce(
          (sum, appointment) => {
            if (!appointment) return sum;

            let amount = 0;

            if (appointment.invoice?.amount !== undefined) {
              amount = appointment.invoice.amount;
            } else if (appointment.service?.price !== undefined) {
              amount = appointment.service.price;
            }

            if (appointment.service?.tax?.isTaxable) {
              amount = amount * (1 + (appointment.service.tax.taxRate || 0));
            }

            return sum + amount * payPercentage;
          },
          0
        );

        return total + clinicianTotal;
      },
      0
    );

    return totalPendingPay.toFixed(2);
  };

  const calculateCompletedPay = () => {
    return totalPayrollAmount;
  };

  const calculateCarryoverPay = () => {
    // Check if in carryover mode and allAppointments is valid
    if (!inCarryover || typeof allAppointments !== "object") {
      return 0;
    }

    const totalCarryoverPay = Object.values(allAppointments).reduce(
      (total, clinicianData) => {
        const { appointments, clinician } = clinicianData;
        if (!appointments || !clinician) return total;

        const payPercentage = clinician.defaultPayPercentage ? clinician.defaultPayPercentage / 100 : 0.45;

        const clinicianTotal = appointments.reduce((sum, appointment) => {
          let amount = 0;
          if (appointment.invoice?.amount !== undefined) {
            amount = appointment.invoice.amount;
          } else if (appointment.service?.price !== undefined) {
            amount = appointment.service.price;
          }

          // Apply tax if applicable
          if (appointment.service?.tax?.isTaxable) {
            amount = amount * (1 + (appointment.service.tax.taxRate || 0));
          }

          return sum + (amount * payPercentage);
        }, 0);

        return total + clinicianTotal;
      },
      0
    );

    return totalCarryoverPay.toFixed(2);
  };

  // Memoize metrics calculation first since other calculations depend on it
  const metrics = useMemo(() => {
    const metrics = {
      clinicianMetrics: {},
      totalCompletedAppointments: 0,
      totalPayrollAmount: 0,
    };

    if (!Array.isArray(allPayrolls)) {
      console.error("allPayrolls is not an array:", allPayrolls);
      return metrics;
    }

    allPayrolls.forEach((entry) => {
      if (!entry || !entry.clinician || !entry.clinician._id) {
        console.warn("Invalid payroll entry or clinician:", entry);
        return;
      }

      const clinicianId = entry.clinician._id;
      const payrolls = entry.payrolls || [];

      if (!metrics.clinicianMetrics[clinicianId]) {
        metrics.clinicianMetrics[clinicianId] = {
          completedAppointments: 0,
          payrollAmount: 0,
          readyCount: 0,
          readyAmount: 0,
          paidCount: 0,
          paidAmount: 0,
        };
      }

      payrolls.forEach((payroll) => {
        metrics.clinicianMetrics[clinicianId].completedAppointments += 1;
        metrics.clinicianMetrics[clinicianId].payrollAmount +=
          parseFloat(payroll.payrollAmount) || 0;

        if (payroll.status === "Ready") {
          metrics.clinicianMetrics[clinicianId].readyCount += 1;
          metrics.clinicianMetrics[clinicianId].readyAmount +=
            parseFloat(payroll.payrollAmount) || 0;
        }
        if (payroll.status === "Paid to Clinician") {
          metrics.clinicianMetrics[clinicianId].paidCount += 1;
          metrics.clinicianMetrics[clinicianId].paidAmount +=
            parseFloat(payroll.payrollAmount) || 0;
        }
      });

      metrics.totalCompletedAppointments += payrolls.length;
      metrics.totalPayrollAmount +=
        metrics.clinicianMetrics[clinicianId].payrollAmount;
    });

    // Format amounts to two decimals
    Object.keys(metrics.clinicianMetrics).forEach((id) => {
      metrics.clinicianMetrics[id].payrollAmount =
        metrics.clinicianMetrics[id].payrollAmount.toFixed(2);
      metrics.clinicianMetrics[id].readyAmount =
        metrics.clinicianMetrics[id].readyAmount.toFixed(2);
      metrics.clinicianMetrics[id].paidAmount =
        metrics.clinicianMetrics[id].paidAmount.toFixed(2);
    });

    metrics.totalPayrollAmount = metrics.totalPayrollAmount.toFixed(2);

    return metrics;
  }, [allPayrolls]);

  // Destructure metrics values after they're calculated
  const { clinicianMetrics, totalCompletedAppointments, totalPayrollAmount } = metrics;

  // Memoize pending pay calculation
  const pendingPay = useMemo(() => {
    if (!allAppointments || typeof allAppointments !== "object" || inCarryover) {
      return 0;
    }

    const totalPendingPay = Object.values(allAppointments || {}).reduce(
      (total, clinicianData) => {
        if (!clinicianData || !clinicianData.appointments || !clinicianData.clinician) {
          return total;
        }

        const { appointments, clinician } = clinicianData;
        const payPercentage = clinician?.defaultPayPercentage
          ? clinician.defaultPayPercentage / 100
          : 0;

        const clinicianTotal = (appointments || []).reduce(
          (sum, appointment) => {
            if (!appointment) return sum;

            let amount = 0;

            if (appointment.invoice?.amount !== undefined) {
              amount = appointment.invoice.amount;
            } else if (appointment.service?.price !== undefined) {
              amount = appointment.service.price;
            }

            if (appointment.service?.tax?.isTaxable) {
              amount = amount * (1 + (appointment.service.tax.taxRate || 0));
            }

            return sum + amount * payPercentage;
          },
          0
        );

        return total + clinicianTotal;
      },
      0
    );

    return totalPendingPay.toFixed(2);
  }, [allAppointments, inCarryover]);

  // Memoize carryover pay calculation
  const carryoverPay = useMemo(() => {
    if (!inCarryover || typeof allAppointments !== "object") {
      return 0;
    }

    const totalCarryoverPay = Object.values(allAppointments).reduce(
      (total, clinicianData) => {
        const { appointments, clinician } = clinicianData;
        const payPercentage = clinician.defaultPayPercentage
          ? clinician.defaultPayPercentage / 100
          : 0;

        const clinicianTotal = appointments.reduce((sum, appointment) => {
          const servicePrice = appointment.service?.price || 0;
          const calculatedPay = servicePrice * payPercentage;
          return sum + calculatedPay;
        }, 0);

        return total + clinicianTotal;
      },
      0
    );

    return totalCarryoverPay.toFixed(2);
  }, [allAppointments, inCarryover]);

  // Memoize sorted clinicians
  const sortedClinicians = useMemo(() => {
    return sortOrder === "default"
      ? clinicians
      : [...clinicians].sort((a, b) => {
          const aLetter = a.firstName.charAt(0).toLowerCase();
          const bLetter = b.firstName.charAt(0).toLowerCase();
          if (aLetter === bLetter) return 0;
          return sortOrder === "az"
            ? aLetter > bLetter
              ? 1
              : -1
            : aLetter < bLetter
            ? 1
            : -1;
        });
  }, [clinicians, sortOrder]);

  // Memoize active and inactive clinicians with cohort and clinic filtering
  const { activeClinicians, inactiveClinicians } = useMemo(() => {
    const filterByClinicAndCohort = (clinician) => {
      // Check if clinician has payroll data for this period
      const hasPayrollData = allPayrolls?.some(entry => entry.clinician?._id === clinician._id);
      
      // If no cohort is selected, show all clinicians
      if (!selectedPayrollCohort) {
        // If "All" clinics selected, show all clinicians
        if (selectedClinicId === "All") return true;
        
        // If specific clinic selected, show clinicians who either:
        // 1. Work at that clinic (traditional filtering), OR
        // 2. Have appointment data for that clinic (from API response), OR 
        // 3. Have existing payrolls for this period
        const worksAtClinic = clinician.clinics?.some(clinic => clinic._id === selectedClinicId);
        const hasAppointmentData = allAppointments && allAppointments[clinician._id];
        
        return worksAtClinic || hasAppointmentData || hasPayrollData;
      }
      
      // If cohort is selected, filter clinicians by clinics in that cohort
      const clinicsInCohort = clinics.filter(c => c.payrollCohort?._id === selectedPayrollCohort._id);
      const clinicIdsInCohort = clinicsInCohort.map(c => c._id);
      
      // Check if clinician works at any clinic in the selected cohort
      const clinicianWorksInCohort = clinician.clinics?.some(clinic => 
        clinicIdsInCohort.includes(clinic._id)
      );
      
      // IMPORTANT FIX: If clinician has appointment data or payroll data, show them even if clinic relationship is missing
      const hasAppointmentData = allAppointments && allAppointments[clinician._id];
      
      // Show clinician if they either work in the cohort OR have appointment data OR have payroll data
      if (!clinicianWorksInCohort && !hasAppointmentData && !hasPayrollData) return false;
      
      // If "All" clinics selected within cohort, show all clinicians in cohort
      if (selectedClinicId === "All") return true;
      
      // If specific clinic selected, show clinicians who either work at that clinic OR have appointment data
      const worksAtClinic = clinician.clinics?.some(clinic => clinic._id === selectedClinicId);
      
      return worksAtClinic || hasAppointmentData;
    };

    return {
      activeClinicians: sortedClinicians.filter(
        (clinician) =>
          clinician.payrollType === "fee for service" &&
          clinician.userStatus !== "inactive" &&
          filterByClinicAndCohort(clinician)
      ),
      inactiveClinicians: sortedClinicians.filter(
        (clinician) =>
          clinician.payrollType === "fee for service" &&
          clinician.userStatus === "inactive" &&
          filterByClinicAndCohort(clinician)
      ),
    };
  }, [sortedClinicians, selectedPayrollCohort, selectedClinicId, clinics, allAppointments, allPayrolls]);

  // Memoize stats array
  const stats = useMemo(() => [
    {
      name: `Pay Period ${selectedPeriod.payPeriodNumber}`,
      value: `${selectedPeriod.includeDates}`,
      change: "",
      changeType: "neutral",
    },
    {
      name: "Submit Appointments By End Of",
      value: `${getSubmissionDate()}`,
      change: "",
      changeType: "neutral",
    },
    {
      name: "Pay Day",
      value: `${getScheduledPayDay()}`,
      change: "",
      changeType: "neutral",
    },
    {
      name: "Pending Appointments",
      value: `${calculatePendingAppointments()}`,
      change: "",
      changeType: "neutral",
    },
    {
      name: "Completed Appointments",
      value: `${totalCompletedAppointments}`,
      change: "",
      changeType: "neutral",
    },
    {
      name: "Carryover Appointments",
      value: `${calculateCarryoverAppointments()}`,
      change: "",
      changeType: "neutral",
    },
    {
      name: "Total Pending Pay",
      value: `$${pendingPay}`,
      change: "",
      changeType: "neutral",
    },
    {
      name: "Total Completed Pay",
      value: `$${totalPayrollAmount}`,
      change: "",
      changeType: "neutral",
    },
    {
      name: "Total Carryover Pay",
      value: `$${carryoverPay}`,
      change: "",
      changeType: "neutral",
    },
  ], [selectedPeriod, pendingPay, carryoverPay, totalCompletedAppointments, totalPayrollAmount]);

  const calculatePayForClinician = (appointments, payPercentage) => {
    if (!Array.isArray(appointments)) {
      console.error("Invalid appointments array:", appointments);
      return 0;
    }

    return appointments.reduce((sum, appointment) => {
      if (!appointment) return sum;

      let amount = 0;

      if (appointment.invoice?.amount !== undefined) {
        amount = appointment.invoice.amount;
      } else if (appointment.service?.price !== undefined) {
        amount = appointment.service.price;
      }

      if (appointment.service?.tax?.isTaxable) {
        amount = amount * (1 + (appointment.service.tax.taxRate || 0));
      }

      return sum + amount * payPercentage;
    }, 0);
  };
  
  // Cohort management functions
  const handleCreateCohort = () => {
    setEditingCohort(null);
    setCohortFormData({ name: '', description: '' });
    setShowCohortModal(true);
  };
  
  const handleEditCohort = (cohort) => {
    setEditingCohort(cohort);
    setCohortFormData({ name: cohort.name, description: cohort.description || '' });
    setShowCohortModal(true);
  };
  
  const handleDeleteCohort = async (cohort) => {
    if (!window.confirm(`Are you sure you want to delete the "${cohort.name}" cohort? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await dispatch(deletePayrollCohort(cohort._id)).unwrap();
      // If the deleted cohort was selected, reset to "All"
      if (selectedPayrollCohort?._id === cohort._id) {
        setSelectedPayrollCohort(null);
        setSelectedClinicId("All");
      }
    } catch (error) {
      toast.error(`Failed to delete cohort: ${error}`);
    }
  };
  
  const handleCohortSubmit = async (e) => {
    e.preventDefault();
    if (!cohortFormData.name.trim()) {
      toast.warning('Cohort name is required');
      return;
    }
    
    setCohortModalLoading(true);
    try {
      if (editingCohort && editingCohort._id) {
        // Update existing cohort
        await dispatch(updatePayrollCohort({
          id: editingCohort._id,
          cohortData: cohortFormData
        })).unwrap();
      } else {
        // Create new cohort
        await dispatch(createPayrollCohort(cohortFormData)).unwrap();
      }
      setShowCohortModal(false);
      setEditingCohort(null); // Reset editing state
    } catch (error) {
      toast.error(`Failed to ${editingCohort && editingCohort._id ? 'update' : 'create'} cohort: ${error}`);
    } finally {
      setCohortModalLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50/30">
      <div className="relative">
        {/* Loading overlay */}
        {loading && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-2">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600"></div>
              <p className="text-sm text-gray-500">Loading payroll data...</p>
            </div>
          </div>
        )}

        {/* Main content */}
        <div
          className={`py-6 ${loading ? "pointer-events-none opacity-50" : ""}`}>
          <div className="mx-auto max-w-[90rem] px-4 sm:px-6 lg:px-8">
            {/* Stats Grid - Using PayrollStats component */}
            <PayrollStats stats={stats} />

            {/* Controls section - More compact */}
            <div className="mt-4 bg-white p-3 rounded-sm shadow-sm ring-1 ring-gray-900/5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                  {/* Pay period selector */}
                  <Listbox value={selectedPeriod} onChange={handlePeriodChange}>
                    <div className="relative w-full min-w-[250px] max-w-[350px]">
                      <Listbox.Label className="block text-xs font-medium text-gray-700">
                        Select Payroll Period
                      </Listbox.Label>
                      <Listbox.Button className="relative mt-1 w-full cursor-pointer rounded border border-gray-200 bg-white py-1.5 pl-2 pr-8 text-left shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                        <span className="block truncate text-sm">
                          {`${selectedPeriod.payPeriodNumber} (${selectedPeriod.includeDates})`}
                        </span>
                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5">
                          <ChevronUpDownIcon
                            className="h-4 w-4 text-gray-400"
                            aria-hidden="true"
                          />
                        </span>
                      </Listbox.Button>
                      <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded border border-gray-200 bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        {payrollDates.map((period) => (
                          <Listbox.Option
                            key={period.payPeriodNumber}
                            value={period}
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-1.5 pl-2 pr-8 ${
                                active
                                  ? "bg-gray-50 text-gray-900"
                                  : "text-gray-900"
                              }`
                            }>
                            {({ selected }) => (
                              <>
                                <span
                                  className={`block truncate ${
                                    selected ? "font-medium" : "font-normal"
                                  }`}>
                                  {`${period.payPeriodNumber} (${period.includeDates})`}
                                </span>
                                {selected && (
                                  <span className="absolute inset-y-0 right-0 flex items-center pr-2 text-indigo-600">
                                    <CheckIcon
                                      className="h-4 w-4"
                                      aria-hidden="true"
                                    />
                                  </span>
                                )}
                              </>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </div>
                  </Listbox>

                  {/* Payroll Cohort selector */}
                  <div className="flex items-end gap-2">
                    <Listbox value={selectedPayrollCohort} onChange={handleCohortChange}>
                      <div className="relative w-full min-w-[160px] max-w-[200px]">
                        <Listbox.Label className="block text-xs font-medium text-gray-700 flex items-center gap-1">
                          Payroll Cohort
                          <button
                            type="button"
                            onClick={handleCreateCohort}
                            className="inline-flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                            title="Manage Payroll Cohorts">
                            <InformationCircleIcon className="h-3 w-3" />
                          </button>
                        </Listbox.Label>
                      <Listbox.Button className="relative mt-1 w-full cursor-pointer rounded border border-gray-200 bg-white py-1.5 pl-2 pr-8 text-left shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                        <span className="block truncate text-sm">
                          {selectedPayrollCohort ? selectedPayrollCohort.name : "All"}
                        </span>
                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5">
                          <ChevronUpDownIcon
                            className="h-4 w-4 text-gray-400"
                            aria-hidden="true"
                          />
                        </span>
                      </Listbox.Button>
                      <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded border border-gray-200 bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <Listbox.Option
                          key="all"
                          value={null}
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-1.5 pl-2 pr-8 ${
                              active ? "bg-gray-50 text-gray-900" : "text-gray-900"
                            }`
                          }>
                          {({ selected }) => (
                            <>
                              <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>
                                All
                              </span>
                              {selected && (
                                <span className="absolute inset-y-0 right-0 flex items-center pr-2 text-indigo-600">
                                  <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                </span>
                              )}
                            </>
                          )}
                        </Listbox.Option>
                        {payrollCohorts.map((cohort) => (
                          <Listbox.Option
                            key={cohort._id}
                            value={cohort}
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-1.5 pl-2 pr-8 ${
                                active ? "bg-gray-50 text-gray-900" : "text-gray-900"
                              }`
                            }>
                            {({ selected }) => (
                              <>
                                <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>
                                  {cohort.name}
                                </span>
                                {selected && (
                                  <span className="absolute inset-y-0 right-0 flex items-center pr-2 text-indigo-600">
                                    <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                  </span>
                                )}
                              </>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                      </div>
                    </Listbox>
                  </div>

                  {/* Clinic selector */}
                  <Listbox value={selectedClinicId} onChange={setSelectedClinicId}>
                    <div className="relative w-full min-w-[350px]">
                      <Listbox.Label className="block text-xs font-medium text-gray-700">
                        Clinic {selectedPayrollCohort && (
                          <span className="text-indigo-600">({selectedPayrollCohort.name} only)</span>
                        )}
                      </Listbox.Label>
                      <Listbox.Button className="relative mt-1 w-full cursor-pointer rounded border border-gray-200 bg-white py-1.5 pl-2 pr-8 text-left shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                        <span className="block truncate text-sm">
                          {selectedClinicId === "All" ? "All Clinics" : 
                           clinics.find(c => c._id === selectedClinicId)?.name || "Unknown"}
                        </span>
                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5">
                          <ChevronUpDownIcon
                            className="h-4 w-4 text-gray-400"
                            aria-hidden="true"
                          />
                        </span>
                      </Listbox.Button>
                      <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded border border-gray-200 bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <Listbox.Option
                          key="All"
                          value="All"
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-1.5 pl-2 pr-8 ${
                              active ? "bg-gray-50 text-gray-900" : "text-gray-900"
                            }`
                          }>
                          {({ selected }) => (
                            <>
                              <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>
                                All Clinics
                              </span>
                              {selected && (
                                <span className="absolute inset-y-0 right-0 flex items-center pr-2 text-indigo-600">
                                  <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                </span>
                              )}
                            </>
                          )}
                        </Listbox.Option>
                        {clinics
                          .filter(clinic => !selectedPayrollCohort || clinic.payrollCohort?._id === selectedPayrollCohort._id)
                          .map((clinic) => (
                          <Listbox.Option
                            key={clinic._id}
                            value={clinic._id}
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-1.5 pl-2 pr-8 ${
                                active ? "bg-gray-50 text-gray-900" : "text-gray-900"
                              }`
                            }>
                            {({ selected }) => (
                              <>
                                <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>
                                  {clinic.name}
                                </span>
                                {selected && (
                                  <span className="absolute inset-y-0 right-0 flex items-center pr-2 text-indigo-600">
                                    <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                  </span>
                                )}
                              </>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </div>
                  </Listbox>

                  {/* Sort selector - Simplified */}
                  <Listbox value={sortOrder} onChange={setSortOrder}>
                    <div className="relative w-full min-w-[80px] max-w-[100px]">
                      <Listbox.Label className="block text-xs font-medium text-gray-700">
                        Sort Clinicians
                      </Listbox.Label>
                      <Listbox.Button className="relative mt-1 w-full cursor-pointer rounded border border-gray-200 bg-white py-1.5 pl-2 pr-8 text-left shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                        <span className="block truncate text-sm">
                          {sortOrder === "default"
                            ? "Default"
                            : sortOrder === "az"
                            ? "A → Z"
                            : "Z → A"}
                        </span>
                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5">
                          <ChevronUpDownIcon
                            className="h-4 w-4 text-gray-400"
                            aria-hidden="true"
                          />
                        </span>
                      </Listbox.Button>
                      <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded border border-gray-200 bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        {["default", "az", "za"].map((option) => (
                          <Listbox.Option
                            key={option}
                            value={option}
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-1.5 pl-2 pr-8 ${
                                active
                                  ? "bg-gray-50 text-gray-900"
                                  : "text-gray-900"
                              }`
                            }>
                            {({ selected }) => (
                              <>
                                <span
                                  className={`block truncate ${
                                    selected ? "font-medium" : "font-normal"
                                  }`}>
                                  {option === "default"
                                    ? "Default"
                                    : option === "az"
                                    ? "A → Z"
                                    : "Z → A"}
                                </span>
                                {selected && (
                                  <span className="absolute inset-y-0 right-0 flex items-center pr-2 text-indigo-600">
                                    <CheckIcon
                                      className="h-4 w-4"
                                      aria-hidden="true"
                                    />
                                  </span>
                                )}
                              </>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </div>
                  </Listbox>
                </div>

                {/* Action buttons - Simplified */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => exportToSpreadsheet(allPayrolls)}
                    className="inline-flex items-center gap-1.5 rounded border border-gray-200 bg-white px-2.5 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50">
                    <svg
                      className="h-4 w-4 text-gray-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                      />
                    </svg>
                    Export
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPayrollSchedule(!showPayrollSchedule)}
                    className="inline-flex items-center gap-1.5 rounded border border-indigo-600 bg-indigo-600 px-2.5 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-indigo-500">
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
                      />
                    </svg>
                    Schedule
                  </button>
                </div>
              </div>
            </div>

            {/* Clinician sections */}
            <div className="mt-6 space-y-6">
              {/* Active Clinicians */}
              <section>
                <div className="mb-4 flex items-center gap-4">
                  <div className="h-px flex-1 bg-gray-200"></div>
                  <h2 className="text-base font-medium text-gray-900">
                    Active Clinicians ({activeClinicians.length})
                  </h2>
                  <div className="h-px flex-1 bg-gray-200"></div>
                </div>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 xl:grid-cols-4">
                  {activeClinicians.map((clinician) => {
                    const clinicianId = clinician._id;
                    const clinicianData = clinicianMetrics[clinicianId] || {
                      completedAppointments: 0,
                      payrollAmount: "0.00",
                      readyCount: 0,
                      readyAmount: "0.00",
                      paidCount: 0,
                      paidAmount: "0.00",
                    };


                    const pendingAppointments =
                      allAppointments[clinicianId]?.appointments || [];
                    const carryoverAppointments =
                      (inCarryover &&
                        allAppointments[clinicianId]?.appointments) ||
                      0;

                    return (
                      <div
                        key={clinicianId}
                        onClick={() => handleClinicianClick(clinician)}
                        className="group cursor-pointer overflow-hidden rounded-sm bg-white shadow-sm ring-1 ring-gray-200 transition-all hover:shadow-md hover:ring-indigo-500 hover:border-indigo-500">
                        <div className="flex items-center justify-between border-b border-gray-100 bg-gray-100 px-4 py-3">
                          <h3 className="text-sm font-medium text-gray-900">
                            {clinician.firstName} {clinician.lastName}
                          </h3>
                          <span className="rounded-sm px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200">
                            Active
                          </span>
                        </div>
                        <div className="divide-y divide-gray-100">
                          {inCarryover ? (
                            <div className="flex items-center justify-between px-4 py-2.5 transition-colors group-hover:bg-gray-50/50">
                              <span className="text-sm text-gray-600">
                                {carryoverAppointments?.length || 0} Carryover
                              </span>
                              <div className="flex flex-col items-end">
                                <span className="text-xs text-gray-600">
                                  $
                                  {calculatePayForClinician(
                                    carryoverAppointments || [],
                                    clinician.defaultPayPercentage / 100
                                  ).toFixed(2)}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between px-4 py-2.5 transition-colors group-hover:bg-gray-50/50">
                              <span className="text-sm text-gray-600">
                                {pendingAppointments?.length || 0} Pending
                              </span>
                              <div className="flex flex-col items-end">
                                <span className="text-xs text-gray-600">
                                  $
                                  {calculatePayForClinician(
                                    pendingAppointments || [],
                                    clinician.defaultPayPercentage / 100
                                  ).toFixed(2)}
                                </span>
                              </div>
                            </div>
                          )}
                          <div className="flex items-center justify-between px-4 py-2.5 transition-colors group-hover:bg-gray-50/50">
                            <span className="text-sm text-gray-600">
                              {clinicianData.completedAppointments} Completed
                            </span>
                            <div className="flex flex-col items-end">
                              <span className="text-xs text-gray-600">
                                ${clinicianData.payrollAmount}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between px-4 py-2.5 transition-colors group-hover:bg-gray-50/50">
                            <span className="text-sm text-gray-600">
                              {clinicianData.readyCount || 0} Ready
                            </span>
                            <div className="flex flex-col items-end">
                              <span className="text-xs text-gray-600">
                                ${clinicianData.readyAmount || "0.00"}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between px-4 py-2.5 transition-colors group-hover:bg-gray-50/50">
                            <span className="text-sm text-gray-600">
                              {clinicianData.paidCount || 0} Paid
                            </span>
                            <div className="flex flex-col items-end">
                              <span className="text-xs text-gray-600">
                                ${clinicianData.paidAmount || "0.00"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Inactive Clinicians */}
              {inactiveClinicians.length > 0 && (
                <section>
                  <div className="mb-4 flex items-center gap-4">
                    <div className="h-px flex-1 bg-gray-200"></div>
                    <div className="flex items-center gap-3">
                      <h2 className="text-base font-medium text-gray-900">
                        Inactive Clinicians ({inactiveClinicians.length})
                      </h2>
                      <button
                        onClick={() =>
                          setShowInactiveClinicians(!showInactiveClinicians)
                        }
                        className="inline-flex items-center gap-1.5 rounded-sm bg-white px-2.5 py-1.5 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-gray-300 transition-colors hover:bg-gray-50">
                        {showInactiveClinicians ? (
                          <>
                            <XMarkIcon className="h-4 w-4" />
                            Hide
                          </>
                        ) : (
                          <>
                            <ChevronUpDownIcon className="h-4 w-4" />
                            Show
                          </>
                        )}
                      </button>
                    </div>
                    <div className="h-px flex-1 bg-gray-200"></div>
                  </div>
                  {showInactiveClinicians && (
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 xl:grid-cols-4">
                      {inactiveClinicians.map((clinician) => {
                        const clinicianId = clinician._id;
                        const clinicianData = clinicianMetrics[clinicianId] || {
                          completedAppointments: 0,
                          payrollAmount: "0.00",
                          readyCount: 0,
                          readyAmount: "0.00",
                          paidCount: 0,
                          paidAmount: "0.00",
                        };

                        const pendingAppointments =
                          allAppointments[clinicianId]?.appointments || [];
                        const carryoverAppointments =
                          (inCarryover &&
                            allAppointments[clinicianId]?.appointments) ||
                          0;

                        return (
                          <div
                            key={clinicianId}
                            onClick={() => handleClinicianClick(clinician)}
                            className="group cursor-pointer overflow-hidden rounded-sm bg-white shadow-sm ring-1 ring-gray-200 transition-all hover:shadow-md hover:ring-indigo-300">
                            <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50/50 px-4 py-3">
                              <h3 className="text-sm font-medium text-gray-900">
                                {clinician.firstName} {clinician.lastName}
                              </h3>
                              <span
                                className={`rounded-sm px-2 py-0.5 text-xs font-medium ${
                                  clinician.userStatus === "inactive"
                                    ? "bg-red-100 text-red-700 border border-red-200"
                                    : "bg-emerald-100 text-emerald-700 border border-emerald-200"
                                }`}>
                                {clinician.userStatus === "inactive"
                                  ? "Inactive"
                                  : "Active"}
                              </span>
                            </div>
                            <div className="divide-y divide-gray-100">
                              {inCarryover ? (
                                <div className="flex items-center justify-between px-4 py-2.5">
                                  <span className="text-sm text-gray-500">
                                    {carryoverAppointments?.length || 0}{" "}
                                    Carryover
                                  </span>
                                  <span className="font-medium text-gray-900">
                                    $
                                    {calculatePayForClinician(
                                      carryoverAppointments || [],
                                      clinician.defaultPayPercentage / 100
                                    ).toFixed(2)}
                                  </span>
                                </div>
                              ) : (
                                <div className="flex items-center justify-between px-4 py-2.5">
                                  <span className="text-sm text-gray-500">
                                    {pendingAppointments?.length || 0} Pending
                                  </span>
                                  <span className="font-medium text-gray-900">
                                    $
                                    {calculatePayForClinician(
                                      pendingAppointments || [],
                                      clinician.defaultPayPercentage / 100
                                    ).toFixed(2)}
                                  </span>
                                </div>
                              )}
                              <div className="flex items-center justify-between px-4 py-2.5">
                                <span className="text-sm text-gray-500">
                                  {clinicianData.completedAppointments}{" "}
                                  Completed
                                </span>
                                <span className="font-medium text-gray-900">
                                  ${clinicianData.payrollAmount}
                                </span>
                              </div>
                              <div className="flex items-center justify-between px-4 py-2.5">
                                <span className="text-sm text-gray-500">
                                  {clinicianData.readyCount || 0} Ready
                                </span>
                                <span className="font-medium text-gray-900">
                                  ${clinicianData.readyAmount || "0.00"}
                                </span>
                              </div>
                              <div className="flex items-center justify-between px-4 py-2.5">
                                <span className="text-sm text-gray-500">
                                  {clinicianData.paidCount || 0} Paid to
                                  Clinician
                                </span>
                                <span className="font-medium text-gray-900">
                                  ${clinicianData.paidAmount || "0.00"}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showPayrollSchedule && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          onClick={handleCloseModal}>
          <div className="flex min-h-screen items-end justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500/75" />
            <div className="inline-block transform overflow-hidden rounded-sm bg-white px-4 pb-4 pt-5 text-left align-bottom shadow-lg transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6 sm:align-middle">
              <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                <button
                  type="button"
                  className="rounded-sm bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  onClick={handleCloseModal}>
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <PayrollScheduleTable />
            </div>
          </div>
        </div>
      )}

      {ishrpayrollpage && selectedClinician && (
        <HRIndividualPayrollPage
          clinician={selectedClinician}
          selectedPeriod={selectedPeriod}
          selectedClinicId={selectedClinicId}
          selectedPayrollCohort={selectedPayrollCohort}
          isOpen={ishrpayrollpage}
          onClose={() => {
            setishrpayrollpage(false);
          }}
          onUpdateSuccess={() => {
            setRefreshKey((prev) => prev + 1);
            setishrpayrollpage(false);
            setTimeout(() => {
              setishrpayrollpage(true);
            }, 0);
          }}
        />
      )}
      
      {/* Cohort Management Modal */}
      <Transition appear show={showCohortModal} as={React.Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowCohortModal(false)}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden bg-white p-6 align-middle shadow-xl transition-all rounded-sm">
                  <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      {editingCohort ? 'Edit Payroll Cohort' : 'Manage Payroll Cohorts'}
                    </Dialog.Title>
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500/20"
                      onClick={() => setShowCohortModal(false)}>
                      <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>

                  <div className="mt-4">
                    {!editingCohort ? (
                      /* Cohort List View */
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-gray-600">
                            Manage payroll cohorts for organizing clinics into processing groups.
                          </p>
                          <button
                            type="button"
                            onClick={() => {
                              setCohortFormData({ name: '', description: '' });
                              setEditingCohort({ _id: null }); // Set as new cohort object
                            }}
                            className="inline-flex items-center gap-1.5 rounded border border-indigo-600 bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-indigo-500">
                            <PlusIcon className="h-4 w-4" />
                            Add Cohort
                          </button>
                        </div>
                        
                        <div className="divide-y divide-gray-200">
                          {payrollCohorts.map((cohort) => (
                            <div key={cohort._id} className="flex items-center justify-between py-3">
                              <div>
                                <h4 className="text-sm font-medium text-gray-900">{cohort.name}</h4>
                                {cohort.description && (
                                  <p className="text-sm text-gray-500">{cohort.description}</p>
                                )}
                                <p className="text-xs text-gray-400">
                                  {clinics.filter(c => c.payrollCohort?._id === cohort._id).length} clinics assigned
                                </p>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleEditCohort(cohort)}
                                  className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50">
                                  <PencilIcon className="h-3 w-3" />
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteCohort(cohort)}
                                  className="inline-flex items-center gap-1 rounded border border-red-200 bg-white px-2 py-1 text-xs font-medium text-red-700 shadow-sm transition-colors hover:bg-red-50">
                                  <TrashIcon className="h-3 w-3" />
                                  Delete
                                </button>
                              </div>
                            </div>
                          ))}
                          {payrollCohorts.length === 0 && (
                            <div className="py-8 text-center">
                              <p className="text-sm text-gray-500">No payroll cohorts found.</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      /* Cohort Form */
                      <form onSubmit={handleCohortSubmit} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Cohort Name *
                          </label>
                          <input
                            type="text"
                            value={cohortFormData.name}
                            onChange={(e) => setCohortFormData(prev => ({ ...prev, name: e.target.value }))}
                            className="mt-1 block w-full border border-gray-200 bg-white py-2 pl-3 pr-3 text-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="e.g., Hearsay, Kekoa Tree"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Description
                          </label>
                          <textarea
                            value={cohortFormData.description}
                            onChange={(e) => setCohortFormData(prev => ({ ...prev, description: e.target.value }))}
                            className="mt-1 block w-full border border-gray-200 bg-white py-2 pl-3 pr-3 text-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            rows="3"
                            placeholder="Optional description for this cohort"
                          />
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                          <button
                            type="button"
                            onClick={() => setEditingCohort(null)}
                            className="inline-flex items-center gap-1.5 border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50">
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={cohortModalLoading}
                            className="inline-flex items-center gap-1.5 border border-indigo-600 bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-500 disabled:opacity-50">
                            {cohortModalLoading ? 'Saving...' : (editingCohort && editingCohort._id ? 'Update' : 'Create')}
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
